import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";

const Fruits = [
    "apple.png", "banana.png", "orange.png", "mango.png", "grapes.png", "straberry.png", "watermelon.png", "pineapple.png", "papaya.png", "cherry.png", "pomegranate.png", "pear.png", "guava.png", "blueberry.png", "lemon.png", "peach.png", "coconut.png", "kiwi.png", "plum.png", "dragonfruit.png"
];

const FruitsNames = Fruits.map(fruit => fruit.replace(".png", ""));

function shuffleArray<T>(array: T[]): T[] {
    return [...array].sort(() => Math.random() - 0.5);
}

type QuestionType = {
    text: string;
    correctAnimal: string;
    correctImage: string;
    allImages: string[];
};

const getDynamicSizes = () => {
    const screenWidth = window.innerWidth;
    return { imageSize: screenWidth > 1024 ? 120 : screenWidth > 600 ? 100 : 80 };
};

const FindFruit = () => {
    const [question, setQuestion] = useState<QuestionType | null>(null);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [feedback, setFeedback] = useState<string | null>(null);
    const [imageSize, setImageSize] = useState(getDynamicSizes().imageSize);
    const [showImage, setShowImage] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        const handleResize = () => setImageSize(getDynamicSizes().imageSize);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const generateRandomQuestion = (remainingImages: number): QuestionType => {
        const shuffledAnimals = shuffleArray([...Fruits]);
        const correctAnimal = FruitsNames[Math.floor(Math.random() * FruitsNames.length)];
        const correctImage = `${correctAnimal}.png`;

        const incorrectImages = shuffledAnimals.filter(img => img !== correctImage).slice(0, remainingImages);
        const allImages = shuffleArray([correctImage, ...incorrectImages]);

        return {
            text: `Select the image of: ${correctAnimal.toUpperCase()}`,
            correctAnimal,
            correctImage,
            allImages,
        };
    };

    const generateQuestion = useCallback(() => {
        if (!containerRef.current) return;

        setTimeout(() => {
            const container = containerRef.current?.getBoundingClientRect();
            if (!container) return;

            const columns = Math.floor(container.width / (imageSize + 20));
            const rows = Math.floor(container.height / (imageSize + 20));
            const totalImage = columns * rows;
            const remainingImages = totalImage > 1 ? totalImage - 1 : 0;

            setQuestion(generateRandomQuestion(remainingImages));
            setSelectedImage(null);
            setFeedback(null);
        }, 1000);
    }, [imageSize]);

    useEffect(() => {
        generateQuestion();
        document.title = "Find the Fruit – Nursery Worksheet | Mama’s Guidance"; // Set page title
    }, [generateQuestion]);

    const handleImageClick = (image: string) => {
        if (!question) return;

        setSelectedImage(image);
        setFeedback(image === question.correctImage ? "Right" : "Wrong");

        if (image === question.correctImage) {
            setShowImage(true)
            setTimeout(() => {
                setSelectedImage(null);
                setFeedback(null);
                setQuestion(null);
                generateQuestion();
                setShowImage(false)
            }, 2000);
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Make fruit learning fun with the "Find the Fruit" activity on Mamas Guidance—an interactive game for nursery kids to identify and recognize different fruits, helping build observation skills and healthy food awareness.' />
            </Helmet>
            <div className="contentPart additionWord  match-container" style={{ marginTop: "0%" }}>
                <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                <IonModal
                    ref={modal}
                    trigger="open-modal"
                    initialBreakpoint={1}
                    breakpoints={[0, 0.5, 0.75, 1]}
                    handleBehavior="cycle"
                    backdropDismiss={false}  // ✅ Prevents click-to-close
                    className="full-page-modal"
                >
                    <IonContent className="ion-padding">
                        <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                        <h1>Find the Fruit</h1>

                        <p>
                            Help your little learner explore the world of fruits with our <strong>Find the Fruit</strong> worksheet! This interactive activity is perfect for nursery and preschool children to <strong>identify, recognize, and name fruits</strong> through fun visuals.
                        </p>

                        <h2>🍎 What’s in the Worksheet?</h2>
                        <p>
                            The worksheet displays colorful images of common fruits like <b><em>apple, banana, mango, grapes, watermelon</em></b>, and more. Each question prompts the child to <strong>find a specific fruit</strong> and either circle, color, or tick the correct option.
                        </p>

                        <h2>🎯 Learning Objectives</h2>
                        <ul>
                            <li>Recognize different fruits by shape, color, and appearance</li>
                            <li>Learn fruit names and improve vocabulary</li>
                            <li>Strengthen observation and memory skills</li>
                            <li>Build awareness of healthy food and environmental science</li>
                        </ul>

                        <h2>👩‍🏫 How to Use This Worksheet</h2>
                        <ul>
                            <li>Read the prompt: <strong>“Find the mango”</strong> or <strong>“Circle the grapes.”</strong></li>
                            <li>Let the child scan the pictures and pick the correct fruit.</li>
                            <li>Encourage them to say the fruit’s name aloud.</li>
                            <li>After the activity, show them the real fruit (if available) for reinforcement.</li>
                        </ul>


                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/nursery/environmental-science/animal/find-animal"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Find Animal</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/nursery/environmental-science/vegetables/find-vegetable"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Find Vegetables</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/nursery/environmental-science/body-parts/choose-correct"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Identify Correct Body Parts</IonLabel>
                            </IonItem>
                        </div>

                        <h2>🧠 Why This Activity Matters</h2>
                        <p>
                            Learning to identify fruits builds <strong>essential observation skills</strong> and <strong>lays the foundation for healthy eating</strong>. It also improves early science knowledge by connecting food to nature.
                        </p>

                        <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                    </IonContent>
                </IonModal>
                <article className="visually-hidden">
                    <h1>Find the Fruit</h1>

                    <p>
                        Help your little learner explore the world of fruits with our <strong>Find the Fruit</strong> worksheet! This interactive activity is perfect for nursery and preschool children to <strong>identify, recognize, and name fruits</strong> through fun visuals.
                    </p>

                    <h2>🍎 What’s in the Worksheet?</h2>
                    <p>
                        The worksheet displays colorful images of common fruits like <em>apple, banana, mango, grapes, watermelon</em>, and more. Each question prompts the child to <strong>find a specific fruit</strong> and either circle, color, or tick the correct option.
                    </p>

                    <h2>🎯 Learning Objectives</h2>
                    <ul>
                        <li>Recognize different fruits by shape, color, and appearance</li>
                        <li>Learn fruit names and improve vocabulary</li>
                        <li>Strengthen observation and memory skills</li>
                        <li>Build awareness of healthy food and environmental science</li>
                    </ul>

                    <h2>👩‍🏫 How to Use This Worksheet</h2>
                    <ul>
                        <li>Read the prompt: “Find the mango” or “Circle the grapes.”</li>
                        <li>Let the child scan the pictures and pick the correct fruit.</li>
                        <li>Encourage them to say the fruit’s name aloud.</li>
                        <li>After the activity, show them the real fruit (if available) for reinforcement.</li>
                    </ul>


                    <h2>🔗 Related Topics</h2>
                    <div>
                        <IonItem routerLink="/nursery/environmental-science/animal/find-animal"
                            routerDirection="none"
                            lines="none"
                            detail={true}>
                            <IonLabel>Find Animal</IonLabel>
                        </IonItem>
                        <IonItem routerLink="/nursery/environmental-science/vegetables/find-vegetable"
                            routerDirection="none"
                            lines="none"
                            detail={true}>
                            <IonLabel>Find Vegetables</IonLabel>
                        </IonItem>
                        <IonItem routerLink="/nursery/environmental-science/body-parts/choose-correct"
                            routerDirection="none"
                            lines="none"
                            detail={true}>
                            <IonLabel>Identify Correct Body Parts</IonLabel>
                        </IonItem>
                    </div>

                    <h2>🧠 Why This Activity Matters</h2>
                    <p>
                        Learning to identify fruits builds essential observation skills and lays the foundation for healthy eating. It also improves early science knowledge by connecting food to nature.
                    </p>

                </article>
                <h6 style={{ textAlign: "center" }}>{question?.text}</h6>
                <div
                    ref={containerRef}
                    className="chooseImageContainer"
                    style={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        display: "grid",
                        gridTemplateColumns: `repeat(auto-fit, minmax(${imageSize}px, 1fr))`,
                        gap: "10px",
                        justifyContent: "center",
                        alignItems: "center",
                        overflow: "hidden"
                    }}
                >
                    {question?.allImages.map((img, index) => (
                        <div key={index} style={{ textAlign: "center" }}>
                            <img
                                src={`${process.env.REACT_APP_API_URL}${img}`}
                                alt={img.replace(".png", "")}
                                onClick={() => handleImageClick(img)}
                                style={{
                                    width: `${imageSize}px`,
                                    height: `${imageSize}px`,
                                    cursor: "pointer",
                                    border: selectedImage === img ?
                                        (img === question.correctImage ? "5px solid green" : "5px solid red")
                                        : "none",
                                    transition: "transform 0.2s ease-in-out",
                                }}
                            />
                            {selectedImage === img && (
                                <div
                                    style={{
                                        fontWeight: "bold",
                                        color: feedback === "Right" ? "green" : "red",
                                        marginTop: "5px",
                                    }}
                                >
                                    {feedback}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
};

export default FindFruit;
